<template>
  <cf-validated-form name="giftCardAutoReload">
    <Teleport
      defer
      to="#portal-modal-header-target"
    >
      Gift Card Reload Details
    </Teleport>

    <div class="cf-flex cf-flex--column cf-flex--gap-16 cf-padding-y-16">
      <template v-if="autoReloadEnabled">
        <cf-gift-card-amount-select
          v-model="reloadAmount"
          label="Reload Amount"
          class="cf-text-align-left"
          :amounts="merchant.giftCardConfiguration.amounts.map(({ amount }) => amount)"
          :min-value="merchant.giftCardConfiguration.autoReloadMinimumAmount"
          :max-value="merchant.giftCardConfiguration.autoReloadMaximumAmount"
        />

        <p class="cf-subtitle cf-subtitle--2 cf-text-align-left">
          Reload <span class="cf-subtitle--heavy">{{ dollars((reloadAmount || 0), { trimZeros: true }) }}</span> when my balance is below:
        </p>

        <cf-gift-card-amount-select
          v-model="thresholdTriggerAmount"
          label="Threshold Amount"
          hide-label
          :amounts="merchant.giftCardConfiguration.thresholdAmounts.map(({ amount }) => amount)"
          :min-value="merchant.giftCardConfiguration.minimumThresholdAmount"
          :max-value="merchant.giftCardConfiguration.maximumThresholdAmount"
        />
      </template>

      <cf-switch-input
        v-model="autoReloadEnabled"
        name="autoReload"
      >
        <span class="cf-icon cf-icon--heavy far fa-repeat" />

        <span class="cf-subtitle cf-subtitle--2 cf-subtitle--heavy">
          Auto Reload
        </span>
      </cf-switch-input>

      <cf-payment-methods
        v-if="autoReloadEnabled"
        pay-cta-text="Save"
        payment-modal-title="Review"
        payment-total-title="Reload Amount"
        :payment-gateway-override="merchant.defaultPaymentGateway"
        primary-cta-text="Next"
        :hidden-elements="{
          contactInfo: 'read-only',
          giftCards: true,
          roomCharge: true,
          signIn: true,
          smsReceipt: true,
          tendr: true,
        }"
        force-save-cards
        :gift-card-reload-payment-method="{
          method: autoReload.paymentMethod,
          creditCardId: autoReload.paymentCreditCardId
        }"
        :total="reloadAmount"
        :checkout-handler="saveAutoReloadConfig"
      >
        <template #payment-modal-top>
          <cf-expandable-section auto-expand>
            <template #title>
              <h4 class="cf-title cf-title--4">
                Auto Reload Details
              </h4>
            </template>

            <template #content>
              <ul class="cf-flex cf-flex--column cf-flex--align-start cf-flex--gap-12 cf-bullet-list">
                <li class="cf-subtitle cf-subtitle--2">
                  Reload <span class="cf-subtitle--heavy">{{ dollars(reloadAmount, { trimZeros: true }) }}</span>
                  when your card balance is below <span class="cf-subtitle--heavy">{{ dollars(thresholdTriggerAmount, { trimZeros: true }) }}.</span>
                </li>

                <li class="cf-subtitle cf-subtitle--2">Auto reload stops only when you remove it.</li>

                <li class="cf-subtitle cf-subtitle--2">Will charge the payment method selected below.</li>
              </ul>
            </template>
          </cf-expandable-section>
        </template>
      </cf-payment-methods>

      <button
        v-else
        type="button"
        :class="['cf-button cf-button--primary cf-button--lg', loading && 'cf-button--loading']"
        @click="saveAutoReloadConfig()"
      >
        Save
      </button>
    </div>
  </cf-validated-form>
</template>


<script>
  import { mapGetters } from 'vuex';

  import normalizePaymentRequestBody from '@/helpers/normalize-payment-request-body';
  import events from '@/services/events';
  import errorHandlers from '@/services/error-handlers';

  import dollars from '@/filters/dollars';

  import CfValidatedForm from '@/view/components/shared/cf-validated-form/index.vue';
  import CfExpandableSection from '@/view/components/shared/cf-expandable-section/index.vue';
  import CfSwitchInput from '@/view/components/shared/cf-switch-input/index.vue';

  import CfGiftCardAmountSelect from '@/view/components/globals/cf-gift-card-amount-select/index.vue';
  import CfPaymentMethods from '@/view/components/globals/cf-payment-methods/index.vue';


  export default {
    name: 'GiftCardAutoReload',

    components: {
      CfGiftCardAmountSelect,
      CfValidatedForm,
      CfExpandableSection,
      CfSwitchInput,
      CfPaymentMethods
    },

    props: {
      autoReload: {
        type: Object,
        default: () => ({})
      },
      cardId: {
        type: String,
        required: true
      }
    },

    data: () => ({
      reloadAmount: 0,
      autoReloadEnabled: false,
      thresholdTriggerAmount: null,
      loading: false
    }),

    computed: {
      ...mapGetters('merchant', ['merchant'])
    },

    mounted() {
      this.reloadAmount = this.autoReload.reloadAmount || 0;
      this.autoReloadEnabled = this.autoReload.enabled || false;
      this.thresholdTriggerAmount = this.autoReload.thresholdTriggerAmount || null;
    },

    methods: {
      dollars,

      async saveAutoReloadConfig(paymentInformation) {
        try {
          this.loading = true;
          let normalizedPayment;

          /**
          * if autoReload is not enabled, this method will be called
          * with no argument from clicking the "save" button
          */
          if (paymentInformation) {
            normalizedPayment = await normalizePaymentRequestBody({
              paymentGateway: this.merchant.defaultPaymentGateway,
              contactInfo: paymentInformation.contactInfo,
              paymentMethod: paymentInformation.paymentMethod
            });
          }

          await this.$store.dispatch('giftCards/autoReload', {
            cardId: this.cardId,
            enabled: this.autoReloadEnabled,
            thresholdTriggerAmount: this.thresholdTriggerAmount,
            reloadAmount: this.reloadAmount,
            ...normalizedPayment && {
              creditCardId: normalizedPayment.creditCardId,
              paymentType: normalizedPayment.paymentType,
              walletToken: normalizedPayment.walletToken
            }
          });

          this.$closeModal('giftCardAutoReload');

          events.emit('notification', {
            type: 'success',
            message: this.autoReloadEnabled ? 'Auto Reload Preferences Saved' : 'Auto Reload Disabled'
          });
        }

        catch (error) {
          errorHandlers.generic(error);
        }

        finally {
          this.loading = false;
          this.$closeModal('paymentInformation');
        }
      }
    }
  };
</script>
